import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { toast } from 'sonner';

import { Icons } from '@/assets/icons/icons';
import { Input, Tooltip, XStack, YStack } from '@/components/ui';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/Accordion';
import { Badge } from '@/components/ui/Badge';
import { JsonViewer } from '@/components/ui/json-viewer';
import { cn } from '@/lib/utils';
import { handleCopyText } from '@/utils/platform';

import { traceViewerStore } from './trace-viewer.store';
import { formatDuration, formatTimeOnly } from './trace-viewer.utils';

import type { TraceSpan } from './trace-viewer.types';

interface SpanDetailsProps {
  span: TraceSpan;
}

export const SpanDetails = observer(({ span }: SpanDetailsProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const spanType =
    span.tags.find((t) => t.key === 'span.kind')?.value?.toString() || 'server';

  const filteredTags = span.tags.filter(
    (tag) =>
      !searchQuery ||
      tag.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
      String(tag.value).toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <YStack className="h-full overflow-hidden">
      <YStack className="p-4 border-b border-border">
        <span className="text-sm font-semibold text-foreground">
          Span Details
        </span>
        <Badge
          variant="secondary"
          size="sm"
          className="mt-2 font-mono w-fit"
          title={span.operationName}
        >
          {span.operationName}
        </Badge>
      </YStack>

      <YStack className="flex-1 overflow-y-auto">
        <Accordion
          type="multiple"
          defaultValue={['basic-info', 'metadata']}
          className="w-full"
        >
          <AccordionItem value="basic-info" className="border-b border-border">
            <AccordionTrigger className="px-4 py-3 hover:bg-surface-hover">
              <XStack className="justify-between items-center gap-2 flex-1">
                <span className="text-xs text-foreground-subtle uppercase tracking-wide font-medium">
                  Basic Information
                </span>
                <Icons.chevronDown className="h-4 w-4 text-foreground-subtle transition-transform" />
              </XStack>
            </AccordionTrigger>
            <AccordionContent className="px-4 py-2">
              <YStack className="space-y-3">
                <BasicInfoRow label="Type" value={spanType} />
                <BasicInfoRow
                  label="Duration"
                  value={formatDuration(span.duration)}
                />
                <BasicInfoRow
                  label="Start Time"
                  value={formatTimeOnly(span.startTime)}
                />
              </YStack>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="metadata" className="border-b border-border">
            <AccordionTrigger className="px-4 py-3 hover:bg-surface-hover">
              <XStack className="justify-between items-center gap-2 flex-1">
                <span className="text-xs text-foreground-subtle uppercase tracking-wide font-medium">
                  Metadata
                </span>
                <Icons.chevronDown className="h-4 w-4 text-foreground-subtle transition-transform" />
              </XStack>
            </AccordionTrigger>
            <AccordionContent className="px-4 py-2">
              <YStack className="space-y-3">
                {span.tags.length > 5 && (
                  <Input
                    placeholder="Search metadata..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="h-8 text-xs"
                  />
                )}

                {filteredTags.length > 0 ? (
                  <YStack className="space-y-2">
                    {filteredTags.map((tag, index) => (
                      <MetadataRow
                        key={`${tag.key}-${index}`}
                        tagKey={tag.key}
                        value={tag.value}
                      />
                    ))}
                  </YStack>
                ) : (
                  <span className="text-xs text-foreground-subtle italic">
                    No matching metadata
                  </span>
                )}
              </YStack>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </YStack>
    </YStack>
  );
});

interface BasicInfoRowProps {
  label: string;
  value: string;
}

const BasicInfoRow = ({ label, value }: BasicInfoRowProps) => {
  return (
    <XStack className="items-center justify-between">
      <span className="text-xs text-foreground-subtle">{label}</span>
      <span className="text-xs font-medium text-foreground">{value}</span>
    </XStack>
  );
};

// Helper to try parsing JSON from a string value
function tryParseJson(value: string | number | boolean): {
  isJson: boolean;
  parsed: unknown;
} {
  if (typeof value !== 'string') {
    return { isJson: false, parsed: null };
  }

  // Quick check - must start with { or [
  const trimmed = value.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) {
    return { isJson: false, parsed: null };
  }

  try {
    const parsed = JSON.parse(value);
    return { isJson: true, parsed };
  } catch {
    return { isJson: false, parsed: null };
  }
}

interface MetadataValueProps {
  value: string | number | boolean;
}

const MetadataValue = ({ value }: MetadataValueProps) => {
  const { isJson, parsed } = tryParseJson(value);

  if (isJson) {
    return <JsonViewer data={parsed} maxHeight="sm" className="!p-2" />;
  }

  const getValueColor = () => {
    if (typeof value === 'boolean')
      return value ? 'text-green-600' : 'text-red-600';
    if (typeof value === 'number') return 'text-blue-600';
    return 'text-foreground';
  };

  return (
    <span
      className={cn('text-sm font-medium break-all', getValueColor())}
      title={String(value)}
    >
      {String(value)}
    </span>
  );
};

interface MetadataRowProps {
  tagKey: string;
  value: string | number | boolean;
}

const MetadataRow = observer(({ tagKey, value }: MetadataRowProps) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await handleCopyText(String(value));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.info('Copied to clipboard');
    } catch (_error) {
      toast.error('Failed to copy');
    }
  };

  const isSearchMatch =
    !!traceViewerStore.searchQuery.trim().length &&
    (tagKey
      .toLowerCase()
      .includes(traceViewerStore.searchQuery.toLowerCase()) ||
      String(value)
        .toLowerCase()
        .includes(traceViewerStore.searchQuery.toLowerCase()));

  return (
    <YStack
      className={cn(
        'flex-1 gap-0.5 group border-b border-border py-2 pl-2 border-l-2',
        isSearchMatch ? 'border-l-green-400' : 'border-l-transparent'
      )}
    >
      <XStack className="justify-between items-center gap-2">
        <XStack className="items-center gap-2">
          <span className="text-xs text-foreground-subtle" title={tagKey}>
            {tagKey}
          </span>
          {isSearchMatch && (
            <Tooltip label="Matches your search">
              <Icons.search className="h-2 w-2 text-foreground-subtle" />
            </Tooltip>
          )}
        </XStack>

        <button
          className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 group-has-[:focus-visible]:opacity-100 transition-opacity p-1 hover:bg-surface rounded shrink-0"
          title="Copy value"
          onClick={handleCopy}
        >
          {copied ? (
            <Icons.check className="h-3 w-3 text-green-600" />
          ) : (
            <Icons.copy className="h-3 w-3 text-foreground-subtle" />
          )}
        </button>
      </XStack>
      <MetadataValue value={value} />
    </YStack>
  );
});
